// src/core-app/components/Matrix.jsx

/**
 *
 * Core-App page
 *
 * @module components/Matrix
 *
 */
import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import TableChartIcon from '@mui/icons-material/TableChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';

import MatrixGrid from './MatrixGrid';
import MatrixChart from './MatrixChart/MatrixChart';
import LoadingSplash from '../shared/LoadingSplash';
import { Div } from '../../../luci-styled/Styled';

// 📖 data
import {
  isUiLoading,
  getMatrix,
  isHostedWarehouseStale as getIsWarehouseStale,
  isHostedMatrixStale as getIsMatrixStale,
} from '../../ducks/rootSelectors';
// cancel action
import { bookmark } from '../../ducks/actions/stepper.actions';
import { cancelMatrix } from '../../ducks/actions/matrix.actions';
import useAbortController from '../../../hooks/use-abort-controller';
// app size
import { useAppSizeDataContext } from '../../../contexts/CoreAppSizeContext';

// -----------------------------------------------------------------------------
const DEBUG = process.env.REACT_APP_DEBUG_WORKBENCH === 'true';
// -----------------------------------------------------------------------------
/* eslint-disable no-console */
const GRID_HEIGHT_ADJ = 0; // Download button position

const withDataAndSizePred = (matrixPage, height) =>
  typeof height !== 'undefined' &&
  matrixPage !== null &&
  Object.keys(matrixPage).length > 0;

const Matrix = () => {
  // All hooks must be called before any early returns
  const {
    isLoading,
    inErrorState,
    message: messageWhileLoading,
  } = useSelector(isUiLoading);
  const matrixPage = useSelector(getMatrix);
  const isWarehouseStale = useSelector(getIsWarehouseStale);
  const isMatrixStale = useSelector(getIsMatrixStale);
  const { height: coreAppHeight } = useAppSizeDataContext();
  const [withDataAndSize, setWithDataAndSize] = useState(() =>
    withDataAndSizePred(matrixPage, coreAppHeight),
  );
  const [viewMode, setViewMode] = useState('table');
  const dispatch = useDispatch();
  const abortController = useAbortController();

  const showMatrix = !(
    isLoading || inErrorState || !withDataAndSize || isWarehouseStale || isMatrixStale
  );

  useEffect(() => {
    if (!withDataAndSize) {
      setWithDataAndSize(withDataAndSizePred(matrixPage, coreAppHeight));
    }
  }, [withDataAndSize, coreAppHeight, matrixPage]);

  const handleCancel = useCallback(() => {
    abortController.abort();
    dispatch(bookmark('workbench'));
    dispatch(cancelMatrix());
  }, [dispatch, abortController]);

  const handleViewChange = useCallback((_, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  }, []);

  if (DEBUG) {
    console.debug('%c----------------------------------------', 'color:orange');
    console.debug(`%c📋 Matrix loaded state summary:`, 'color:orange', {
      withDataAndSize,
      isLoading,
      isWarehouseStale,
      isMatrixStale,
      coreAppHeight,
      'should render': showMatrix,
    });
  }

  if (!showMatrix) {
    return (
      <LoadingSplash
        title='Processing'
        message={messageWhileLoading || 'Pulling the requested data'}
        cancel={handleCancel}
        turnOffMessaging
      />
    );
  }

  return (
    <Div className='Luci-matrix' style={{ position: 'relative' }}>
      {/* Floating toggle */}
      <div style={{
        position: 'fixed',
        top: 'calc(var(--app-bar-height) + 4px)',
        right: 16,
        zIndex: 1000,
        background: 'rgba(255,255,255,0.9)',
        borderRadius: 4,
        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
      }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewChange}
          size='small'
        >
          <ToggleButton value='table'>
            <TableChartIcon fontSize='small' />
          </ToggleButton>
          <ToggleButton value='chart'>
            <ShowChartIcon fontSize='small' />
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      {/* Table — hidden via CSS to preserve grid state */}
      <div
        className='outer-container'
        style={{ display: viewMode === 'table' ? 'block' : 'none' }}
      >
        <div className='inner-make-scrollable-container'>
          <MatrixGrid
            matrixPage={matrixPage}
            abortController={abortController}
            gridHeight={coreAppHeight + GRID_HEIGHT_ADJ}
          />
        </div>
      </div>

      {/* Chart — mounts on demand */}
      {viewMode === 'chart' && (
        <MatrixChart height={coreAppHeight + GRID_HEIGHT_ADJ} />
      )}
    </Div>
  );
};

Matrix.propTypes = {};
Matrix.defaultProps = {};

export default Matrix;
